import { useEffect, useState } from "react";
import api, { apiError } from "../../../lib/api";
import { toast } from "sonner";
import { useAuth } from "../../../context/AuthContext";
import ConfirmDialog from "../../../components/ConfirmDialog";

const fmt = (v) => (v ? `${v.slice(0, 16).replace("T", " ")} UTC` : "Never");

export function Administrators() {
  const { user } = useAuth();
  const isSuper = Boolean(user?.super_admin);
  const [admins, setAdmins] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", superGrant: false });
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/admins").then((r) => setAdmins(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const doAdd = async () => {
    const email = form.email.trim().toLowerCase();
    await api.post("/admin/admins", { name: form.name.trim(), email, super_admin: form.superGrant });
    toast.success(`${form.superGrant ? "Super admin" : "Admin"} access granted to ${email}. They have been emailed.`);
    setForm({ name: "", email: "", superGrant: false });
    load();
  };
  const doRevoke = async (a) => {
    await api.post(`/admin/admins/${a.user_id}/revoke`);
    toast.success(`Admin access revoked for ${a.email}`);
    load();
  };

  const askAdd = () => {
    const email = form.email.trim().toLowerCase();
    if (!email) return;
    setConfirm({
      title: form.superGrant ? "Grant super admin access" : "Grant admin access",
      message: form.superGrant
        ? `Grant SUPER admin access to ${email}? They will be able to manage the entire platform AND grant or revoke other administrators. They will be notified by email.`
        : `Grant admin access to ${email}? They will be able to manage the platform, and they will be notified by email.`,
      confirmLabel: form.superGrant ? "Grant super admin" : "Grant access",
      danger: false,
      run: doAdd,
    });
  };
  const askRevoke = (a) => setConfirm({
    title: "Revoke admin access",
    message: `Revoke admin access for ${a.email}? They will become a regular member and lose access to the admin console.`,
    confirmLabel: "Revoke access",
    danger: true,
    run: () => doRevoke(a),
  });

  const runConfirm = async () => {
    setBusy(true);
    try { await confirm.run(); setConfirm(null); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-administrators">
      <div className="flex items-baseline gap-3 flex-wrap">
        <span className="t-label">Administrators</span>
        {isSuper && <span className="t-label text-tint" data-testid="viewer-super-badge">You are a super admin</span>}
      </div>
      <p className="t-small text-muted-s mt-2 measure">
        Administrators can manage members, deals, documents, distributions and the public site.
        {isSuper
          ? " As a super admin, you can add or revoke administrators, including granting super admin access to others."
          : " Only super administrators can add or revoke administrators."}
      </p>

      {isSuper && (
        <div className="mt-5 max-w-2xl" data-testid="add-admin-form">
          <div className="flex gap-3 flex-wrap items-center">
            <input className="input-solera flex-1 min-w-[180px]" placeholder="Full name" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="add-admin-name-input" />
            <input className="input-solera flex-1 min-w-[200px]" type="email" placeholder="Email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="add-admin-email-input" />
            <button className="btn-primary" disabled={!form.email.trim()} onClick={askAdd} data-testid="add-admin-submit">
              Add admin
            </button>
          </div>
          <label className="mt-3 flex items-center gap-2 cursor-pointer w-fit" data-testid="add-admin-super-label">
            <input type="checkbox" checked={form.superGrant}
              onChange={(e) => setForm({ ...form, superGrant: e.target.checked })}
              data-testid="add-admin-super-checkbox" />
            <span className="t-cap text-muted-s">Grant as super admin (can manage other administrators)</span>
          </label>
        </div>
      )}

      <div className="mt-6 space-y-0 max-w-2xl" data-testid="admin-list">
        {admins.map((a) => {
          const canRevoke = isSuper && !a.super_admin && a.user_id !== user?.user_id;
          return (
            <div key={a.user_id} className="hairline py-4" data-testid={`admin-row-${a.user_id}`}>
              <div className="flex gap-4 items-baseline flex-wrap">
                <span className="t-small text-bone">{a.name}</span>
                <span className="t-cap text-muted-s">{a.email}</span>
                {a.super_admin && <span className="t-label text-tint" data-testid={`super-admin-badge-${a.user_id}`}>Super admin</span>}
                {canRevoke && (
                  <button onClick={() => askRevoke(a)} className="t-label ml-auto hover:!text-[#E8E6E1]" data-testid={`revoke-admin-${a.user_id}`}>Revoke</button>
                )}
              </div>
              <div className="flex gap-6 mt-1 flex-wrap">
                <span className="t-cap text-muted-s fig" data-testid={`admin-last-login-${a.user_id}`}>Last login: {fmt(a.last_login_at)}</span>
                <span className="t-cap text-muted-s">Added by: {a.granted_by || (a.super_admin ? "Super admin" : "Seed")}</span>
              </div>
            </div>
          );
        })}
        {admins.length === 0 && <p className="t-small text-muted-s">No administrators found.</p>}
      </div>

      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(o) => { if (!o) setConfirm(null); }}
        title={confirm?.title || ""}
        message={confirm?.message || ""}
        confirmLabel={confirm?.confirmLabel || "Confirm"}
        danger={confirm?.danger}
        busy={busy}
        onConfirm={runConfirm} />
    </div>
  );
}
